'use client';

import {Fragment, ReactNode} from 'react';
import Image from 'next/image';
import Markdown from 'markdown-to-jsx';
import {IArticleBody, IArticleBodyComponent, IStrapiBlockNode} from '@/lib/cms/types';
import type {IGameInfo} from '@/services/dev-console/interfaces';
import GameCard from './GameCard';

// --- Strapi rich-text "blocks" renderer ---------------------------------
//
// Strapi returns the `copy` field as a tree of nodes (see IStrapiBlockNode).
// We render it directly to styled JSX instead of treating it as markdown,
// applying the portal-play design tokens (Helvetica Neue, primary-blue links,
// primary-red blockquote accent) with dark-mode variants throughout.
//
// Visual treatment mirrors the polished portal-games article styling: a light
// body weight, a signature brand-gradient blockquote, and generous spacing.
// Base text color/size/weight are set on the wrapper and inherited by blocks.

/** Renders a single `text` leaf node, applying its inline marks. */
function renderText(node: IStrapiBlockNode, key: number): ReactNode {
  const raw = node.text ?? '';

  // Preserve hard line breaks inside a single text node.
  let content: ReactNode = raw;
  if (raw.includes('\n')) {
    const lines = raw.split('\n');
    content = lines.map((line, i) => (
      <Fragment key={i}>
        {line}
        {i < lines.length - 1 && <br />}
      </Fragment>
    ));
  }

  if (node.bold) content = <strong className="font-semibold">{content}</strong>;
  if (node.italic) content = <em>{content}</em>;
  if (node.underline) content = <span className="underline">{content}</span>;
  if (node.strikethrough) content = <span className="line-through">{content}</span>;
  if (node.code) {
    content = (
      <code className="rounded bg-light-gray px-1.5 py-0.5 font-mono text-[0.9em] text-primary-red dark:bg-white/10 dark:text-secondary-red">
        {content}
      </code>
    );
  }

  return <Fragment key={key}>{content}</Fragment>;
}

/** Renders inline content: `text` leaves and `link` nodes. */
function renderInline(children: IStrapiBlockNode[] = []): ReactNode {
  return children.map((child, i) => {
    if (child.type === 'link') {
      return (
        <a
          key={i}
          href={child.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-blue underline-offset-2 hover:underline dark:text-secondary-blue"
        >
          {renderInline(child.children)}
        </a>
      );
    }
    return renderText(child, i);
  });
}

/** Renders the children of a list item, supporting nested lists. */
function renderListItemChildren(children: IStrapiBlockNode[] = []): ReactNode {
  return children.map((child, i) =>
    child.type === 'list' ? renderBlock(child, i) : (
      <Fragment key={i}>{renderInline([child])}</Fragment>
    ),
  );
}

const headingSizes: Record<number, string> = {
  1: 'text-3xl',
  2: 'text-2xl',
  3: 'text-xl',
  4: 'text-lg',
  5: 'text-base',
  6: 'text-sm',
};

/** Renders a single block-level node. */
function renderBlock(node: IStrapiBlockNode, key: number): ReactNode {
  switch (node.type) {
    case 'heading': {
      // The page title is h1, so promote CMS headings: h3→h2, h4→h3, etc.
      // Any h1/h2 in CMS content becomes h2 to preserve a single h1 on the page.
      const raw = Math.min(Math.max(node.level ?? 2, 1), 6);
      const level = raw <= 2 ? 2 : Math.min(raw - 1, 6);
      const Tag = `h${level}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const headingText = (node.children || []).map((c) => c.text || '').join('').trim();
      const headingId = headingText
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      return (
        <Tag
          key={key}
          id={headingId || undefined}
          className={`mb-3 mt-7 font-medium leading-tight first:mt-0 ${headingSizes[level]}`}
        >
          {renderInline(node.children)}
        </Tag>
      );
    }

    case 'paragraph': {
      // Skip empty spacer paragraphs (container handles vertical rhythm).
      const isEmpty =
        !node.children?.length ||
        node.children.every((c) => c.type === 'text' && !(c.text ?? '').trim());
      if (isEmpty) return null;
      return (
        <p key={key} className="leading-relaxed">
          {renderInline(node.children)}
        </p>
      );
    }

    case 'list': {
      const ordered = node.format === 'ordered';
      const Tag = ordered ? 'ol' : 'ul';
      return (
        <Tag
          key={key}
          className={`my-4 space-y-1.5 pl-6 marker:text-primary-red ${
            ordered ? 'list-decimal' : 'list-disc'
          }`}
        >
          {node.children?.map((item, i) => (
            <li key={i} className="pl-1 leading-relaxed">
              {renderListItemChildren(item.children)}
            </li>
          ))}
        </Tag>
      );
    }

    case 'quote':
      return (
        <blockquote
          key={key}
          style={{
            borderLeftWidth: 6,
            borderLeftStyle: 'solid',
            borderImage: 'linear-gradient(to bottom, #EA0449, #0043FF) 1',
          }}
          className="my-6 rounded-r bg-light-gray px-5 py-4 text-lg italic dark:bg-white/5"
        >
          {renderInline(node.children)}
        </blockquote>
      );

    case 'code':
      return (
        <pre
          key={key}
          className="my-5 overflow-x-auto rounded-lg bg-dark-gray p-4 text-sm text-light-gray dark:bg-black"
        >
          <code className="font-mono">
            {node.children?.map((c) => c.text ?? '').join('\n')}
          </code>
        </pre>
      );

    case 'image': {
      if (!node.image?.url) return null;
      return (
        <ArticleImage
          key={key}
          url={node.image.url}
          width={node.image.width}
          height={node.image.height}
          alt={node.image.alternativeText}
        />
      );
    }

    default:
      return null;
  }
}

function ArticleText({text, copy}: {text?: string; copy?: IStrapiBlockNode[]}) {
  // Prefer Strapi blocks; fall back to legacy markdown strings.
  if (copy && copy.length > 0) {
    return (
      <div className="space-y-4 break-words font-helvetica text-base font-light text-dark-gray dark:text-light-gray">
        {copy.map(renderBlock)}
      </div>
    );
  }

  if (text) {
    const cleaned = text.replace(/<\/br\s*[/]?>/gi, '<br/>');
    return (
      <div className="prose break-words font-helvetica prose-headings:font-helvetica prose-headings:font-medium prose-h2:text-2xl prose-h3:text-xl prose-p:font-light prose-a:text-primary-blue prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-[6px] prose-blockquote:border-primary-red prose-blockquote:bg-light-gray prose-blockquote:px-5 prose-blockquote:py-4 prose-blockquote:not-italic prose-li:font-light prose-li:leading-relaxed max-w-none dark:prose-invert">
        <Markdown>{cleaned}</Markdown>
      </div>
    );
  }

  return null;
}

function ArticleImage({
  url,
  width,
  height,
  alt,
}: {
  url: string;
  width?: number;
  height?: number;
  alt?: string;
}) {
  return (
    <figure className="my-6">
      {width && height ? (
        <Image
          src={url}
          width={width}
          height={height}
          sizes="(max-width: 768px) 100vw, 680px"
          alt={alt || ''}
          className="h-auto w-full rounded-lg shadow-sm"
        />
      ) : (
        // Fall back to an unsized image when Strapi omits dimensions.
        // eslint-disable-next-line @next/next/no-img-element
        <img src={url} alt={alt || ''} className="w-full rounded-lg shadow-sm" />
      )}
    </figure>
  );
}

function ArticleVideo({url, poster}: {url: string; poster?: string}) {
  return (
    <div className="my-5">
      <video
        src={url}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        className="w-full rounded"
      />
    </div>
  );
}

function ArticleEmbed({url, type}: {url: string; type: 'Youtube' | 'Vimeo'}) {
  let embedUrl = url;
  if (type === 'Youtube') {
    const videoId = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/,
    )?.[1];
    if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
  } else if (type === 'Vimeo') {
    const videoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
    if (videoId) embedUrl = `https://player.vimeo.com/video/${videoId}`;
  }

  return (
    <div className="my-5 aspect-video">
      <iframe
        src={embedUrl}
        className="h-full w-full rounded"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
        title="Embedded video"
      />
    </div>
  );
}

export default function ArticleBody({
  body,
  inlineGames = {},
  articleTitle = '',
}: {
  body: IArticleBody[];
  inlineGames?: Record<number, IGameInfo>;
  articleTitle?: string;
}) {
  return (
    <div className="space-y-5">
      {body.map((component, index) => {
        switch (component.__component) {
          case IArticleBodyComponent.TEXT:
            if (!component.text && !component.copy?.length) return null;
            return <ArticleText key={index} text={component.text} copy={component.copy} />;

          case IArticleBodyComponent.IMAGE:
            if (!component.image?.data) return null;
            return (
              <ArticleImage
                key={index}
                url={component.image.data.attributes.url}
                width={component.image.data.attributes.width}
                height={component.image.data.attributes.height}
                alt={component.image_alt_text || articleTitle}
              />
            );

          case IArticleBodyComponent.VIDEO:
            if (!component.video?.data) return null;
            return (
              <ArticleVideo
                key={index}
                url={component.video.data.attributes.url}
                poster={component.poster_image?.data?.attributes.url}
              />
            );

          case IArticleBodyComponent.EMBED:
            if (!component.embed_url || !component.embed_type) return null;
            return (
              <ArticleEmbed key={index} url={component.embed_url} type={component.embed_type} />
            );

          case IArticleBodyComponent.INLINE_GAME: {
            if (!component.game_id) return null;
            const game = inlineGames[component.game_id];
            if (!game) return null;
            return (
              <div key={index} className="my-4">
                <GameCard game={game} />
              </div>
            );
          }

          case IArticleBodyComponent.CTA_BUTTON: {
            if (!component.display_text || !component.url) return null;
            const align =
              component.alignment === 'Right'
                ? 'text-right'
                : component.alignment === 'Center'
                  ? 'text-center'
                  : 'text-left';
            return (
              <div key={index} className={`my-5 ${align}`}>
                <a
                  href={component.url}
                  className="inline-block rounded-full bg-gradient-to-r from-primary-red to-primary-blue px-8 py-3 font-helvetica font-medium text-white shadow-md transition-opacity hover:opacity-90"
                >
                  {component.display_text}
                </a>
              </div>
            );
          }

          default:
            return null;
        }
      })}
    </div>
  );
}
